import React from 'react';
import {
  FiBook,
  FiClock,
  FiHelpCircle,
  FiList,
  FiTarget,
  FiCalendar,
  FiBookOpen,
  FiEdit,
  FiUsers,
} from 'react-icons/fi';
import IELTSCourseTips from '../../../components/IELTSCourseTips';

export default function ReadingTips() {
  const overview = [
    { icon: <FiClock />, label: 'Time:', value: '60 minutes' },
    { icon: <FiHelpCircle />, label: 'Questions:', value: '40' },
    { icon: <FiList />, label: 'Passages:', value: '3 long texts (Academic) or several smaller texts (General Training)' },
    { icon: <FiTarget />, label: 'Skills tested:', value: 'reading for main ideas, detail, understanding opinions, following arguments' },
  ];

  const tips = [
    {
      title: 'Understand the Question Types',
      content: 'You will see many kinds of questions:',
      bullets: [
        'True / False / Not Given',
        'Multiple Choice',
        'Matching Headings',
        'Sentence Completion',
        'Summary / Note / Table Completion',
        'Short Answer Questions',
      ],
      highlight: 'Tip: Practice each type so you understand what it wants and how to answer quickly.',
    },
    {
      title: 'Practice Skimming and Scanning',
      content: [
        '<strong>Skimming</strong> → read quickly to get the main idea.',
        '<strong>Scanning</strong> → move your eyes quickly to find specific information (like dates, names, numbers).',
      ],
      highlight: "Tip: Don't read every word slowly — you don't have time!",
    },
    {
      title: 'Improve Your Vocabulary',
      content: [
        'You cannot use a dictionary in the test.',
        'Learn synonyms (words with similar meanings) because the question and the text often use different words.',
        'Keep a vocabulary notebook with common IELTS words and phrases.',
      ],
      highlight: 'Tip: Read academic articles, news, and reports regularly.',
      linkText: 'View Vocabulary Lessons',
      linkPath: '/exams/english/#vocabulary',
      linkCategory: 'vocabulary',
    },
    {
      title: 'Read the Instructions Carefully',
      content: [
        'Sometimes you must use no more than two words or write one word only.',
        'If you write more than allowed, you get zero for that answer.',
      ],
      highlight: 'Tip: Always underline or circle key parts of the instructions.',
    },
    {
      title: 'Use Keywords',
      content: [
        'Find keywords in the question (important nouns, verbs, dates).',
        'Look for those or similar words (synonyms, paraphrasing) in the passage.',
      ],
      highlight: 'Tip: Train yourself to spot and match keywords quickly.',
    },
    {
      title: 'Be Careful with True/False/Not Given',
      content: [
        '<strong>True:</strong> Information is clearly in the text.',
        '<strong>False:</strong> Information is opposite to the text.',
        '<strong>Not Given:</strong> The text does not say.',
      ],
      highlight: "Tip: Don't use your own knowledge — only what's in the passage!",
    },
    {
      title: 'Manage Your Time',
      content: [
        'You have about 20 minutes per passage.',
        "If you get stuck, move on — don't waste too much time on one question.",
        'Answer all questions — no penalties for wrong answers.',
      ],
      highlight: 'Tip: Use a watch to keep track.',
    },
    {
      title: 'Check Spelling and Grammar',
      content: [
        'Wrong spelling = wrong answer.',
        'Watch for plurals and singulars.',
        'Copy words from the text carefully.',
      ],
      highlight: 'Tip: Always check your answers before time runs out.',
    },
    {
      title: 'Practice with Real IELTS Materials',
      content: [
        'Use real IELTS practice tests.',
        'Time yourself like in the real exam.',
        'Review your mistakes and understand why you got them wrong.',
      ],
      highlight: 'Tip: Focus on improving your weak areas.',
      linkText: 'Try Practice Tests',
      linkPath: '/exams/english/#reading',
      linkCategory: 'reading',
    },
  ];

  const extraStrategies = [
    'Underline important parts of the passage as you read.',
    'Predict the answer before looking at the choices.',
    'Stay calm — the reading test is not about understanding every word, but about finding answers.',
    'Work on your reading speed by reading English daily.',
  ];

  const improvementTips = [
    { icon: <FiCalendar />, label: 'Daily practice:', text: 'Read short articles, news, academic texts.' },
    { icon: <FiBookOpen />, label: 'Build stamina:', text: 'Read longer texts to prepare for the exam length.' },
    { icon: <FiEdit />, label: 'Track progress:', text: 'Keep a study notebook — record your scores, mistakes, new words.' },
    { icon: <FiUsers />, label: 'Study together:', text: 'Join a study group or class if you can — discussing strategies helps.' },
  ];

  const aiHelp = {
    title: 'Need help with IELTS Reading strategies?',
    firstMessage: 'I need help improving my IELTS Reading score and understanding the strategies better.',
    context: 'IELTS Reading: 60 minutes, 40 questions, 3 passages. Skills tested: reading for main ideas, detail, understanding opinions, following arguments. Question types: True/False/Not Given, Multiple Choice, Matching Headings, Sentence Completion, Summary/Note/Table Completion, Short Answer Questions.',
  };

  return (
    <IELTSCourseTips
      category="reading"
      icon={<FiBook />}
      title="IELTS Reading: What You Should Know"
      overview={overview}
      tips={tips}
      extraStrategies={extraStrategies}
      improvementTips={improvementTips}
      aiHelp={aiHelp}
    />
  );
}
